import { Config, Helper } from 'config';
import { forEach, find, map, keys, compact, every, invokeMap, join, uniqueId } from 'lodash';

import { forwardPropertiesFrom, resolve, map$, clone, cloneDeep, Magnitude } from 'gear';

import { Xml } from '../../util';
import { Point, Matrix, Bounds } from '../../geometry';
import { Color, Brush } from '../../style';

import { Figure } from '../figure';

import { AnchorGenerator } from '../anchor';
import { Shape } from '../shape';
import { PowerClipContent } from '../power-clip';
import { _toPrimitives } from '../group';

import { TextLayout } from './text-layout';
import { TextProperties } from './properties';
import { TextPart } from './multi-part';


function joinLines(lines) {
  return join(lines,'\r\n');
}

function splitLines(line) {
  return line.split(/\r\n|[\n\v\f\r\x85\u2028\u2029]/);
}

Figure.TextLayout = TextLayout;


export const Text = Figure.Text = Figure.extend(forwardPropertiesFrom(TextProperties,'properties',{ typeName: 'Text',

  type: 'Text',

  Properties: {
    text: { type: 'string', def: 'Text', manualParsing: true, onChange() { this.invalidate(); } },
    properties: {type: 'TextProperties', def: null, manualParsing: true, onChange() { this.invalidate(); } },
    textParts: { type: 'TextPart[]', def: [], manualParsing: true, onChange() { this.invalidate(); } },

    // Used in output figure polyregions and text figures from multi part text, to reference PowerClipContent in the input figure
    // A PowerClipLink has an extra matrix so we are able to transform the content without cloning it
    powerClipLink: { type: 'PowerClipLink', def: null, owned: true, manualParsing: true }
  },

  get isText        () { return true; },
  get isMultiPartText() { return this._textParts.length > 0; },
  get isSimpleText  () { return !this.isMultiPart && !this.isTypeset; },
  get isTypeset     () { return this.lines.length > 1; },
  get isMultiPart   () { return this.isMultiPartText; },
  get isSimple      () { return this.isSimpleText; },
  get isEmpty       () { return this._text === ''; },
  
  _ignoreAttributes : Figure.prototype._ignoreAttributes.concat( keys(TextProperties.prototype.Properties) ),

  containsLinkedResources() {
    return true;
  },

  get lines() {
    return splitLines(this._text);
  },
  set lines( lines_ ) {
    this.invalidate();
    this._text = joinLines(lines_);
  },

  get font() {
    if( ! this.isMultiPart ) {
      return this._properties.font;
    }
    // Multipart (this code will be cleaned later)
    let currentXmlFont = null,
        allEqual = true;

    forEach( this._textParts, tp => {
      const xmlFont = Xml.fontToXml(tp._properties.font); // [TODO] Remove usage of Xml.fontToXml
      if( ! currentXmlFont ) {
        currentXmlFont = xmlFont;
      }
      else if( currentXmlFont !== xmlFont ) {
        allEqual = false;
      }
    });
    return allEqual ? this._textParts[0]._properties.font : null;
  },
  set font( font_ ) {
    if( ! this.isMultiPart ) {
      this._properties.font = font_;
    }
    else {
      this.invalidate();
      forEach( this.textParts, tp => { tp._properties.font = font_; });
    }
  },

  replaceStyle( figure ) {
    this.callParent(figure);
    // [TODO] Check if this is correct with TextParts
    this.properties = figure.properties;
  },

  drawForClipping( context, transform ) {
    this.outputFigure.drawForClipping(context, transform);
  },

  // [TODO][PROP]
  powerClip( v ) {
    const powerClipContents = this._powerClipContents;
    if(v===undefined) {
      return powerClipContents[0];
    }
    powerClipContents.set( v );
    this.powerClipId = v._id;
    return this;
  },

  addPowerClipContents( v ) {
    var id = this.powerClipId || uniqueId("PC");
    var onTextPart = false;
    forEach(this.textParts, tp => { 
      if (tp.powerClipId) {
        id = tp.powerClipId;
        onTextPart = true;
      }
    });

    const powerClipContents = this._powerClipContents;
    const pccN = PowerClipContent.createForFigure(id, v, this);

    let pcc = find(powerClipContents,'_id',id);
    if( ! pcc ) {
      powerClipContents._set(pccN);
      if (!onTextPart)
        this._properties._powerClipId = id;
      pcc = pccN;

      this.invalidate();
    }
    else {
      pcc._figures._add( pccN._figures );

      if( this.usingPowerClipLink() ) {
        this.invalidateView();
      }
      else {
        this.invalidate();
      }
    }
  },
  replaceParts( parts) {
    this.textParts = compact( map( parts, p => {
      const source = p._textPartSource ;
      if( source ) {
        source.replaceWithText(p,this) ;
        return source;
      }
    }));

    this.invalidate();
    return this;
  },

  regroupParts$( parts) {
    const _this = this;
    return Figure.autoCreateMultiPartText$(parts,_this.matrix22,_this.pin) .then( text => {
    _this.text = text.text;
    _this.anchor = text.anchor;
    _this.preMatrix22 = new Matrix();
    _this.matrix22 = text.matrix22;
    _this.pin = text.pin;
    _this.textParts = text.textParts.clear();
    return _this;
    });
  },

  breakIntoParts() {
    return map( this._textParts, p => p.asTextBlock(this) );
  },

  canEditContent() { return ( this.isMultiPart && this._processes.length === 0 ) || this.callParent(); },

  buildXml(parentNode ) { Xml.addObject(parentNode,this,'TextBlock'); },
  setupXml( node ) {
    // Each line is represented in CDL as a <Line>...</Line> entry
    forEach( this.lines, line => {
      Xml.appendElement(node,'Line').textContent = line;
    });
    this._properties.setupXml(node);
    Xml.addObjectArray(node,this._textParts);
    this.callParent(node);
  },
  createEmptyOutput() {
    return Shape.CreateRectangle({center:this.pin,width:this.fontSize,height:this.fontSize,brush:Brush.empty});
  },

  processesAreSupported() {
    return this._processes.canProcess(this);
  },
  supportedLocalMultiPart() {
    const textParts = this._textParts;
    return textParts.length === 0 || every( textParts, t => t.processes.canProcess(t) );
  },
  canBeProcessedLocally() {
    return Config.localText &&
           ! Helper.isIE_let(9) &&
           this.processesAreSupported() &&
           this.supportedLocalMultiPart() &&
           ! this.needsCacheToRender();
  },

  concreteFontSize_: function() {
    const preScale = this._computedPreScale;
    const matrix = this.globalMatrix_();
    const urm = Matrix.m( Matrix.rotate( -matrix.angle() ), matrix );
    // console.log( preScale.x + ' ' + ( preScale.x * urm.s00 ) + ' | ' + preScale.y + ' ' + ( preScale.y * urm.s11 ) );
    return this.fontSize * Math.min( preScale.x * Math.abs(urm.s00), preScale.y * Math.abs(urm.s11) );
  },

  fontDataScale_() {
    // assert( this._fontData );
    return this._fontDataScale;
  },
  fontData$() {
    const me = this;
    const lines = clone(this.lines);
    const manager = Config.GlobalResourceManager;
    return manager.loadFontData$(me.font,me.text,'M') .then( fontData => {
    me._fontData = fontData;

    // Cache information, as a precaution in case somebody changes the model
    // while processing. We need to review when we are unlocking canvas to avoid
    // actions to interfere one another
    me._fontDataScale = fontData.scaleForSize(me.fontSize,me.fontAutoSize);
    me._fontDataLines = lines;
    return fontData;
    });
  },
  inputPrimitiveFigures$() {
    const _this = this;

    if( this.isEmpty ) {
      _this.font.cache$();
      return resolve({ figures: [] });
    }

    const textParts = _this._textParts;
    if( textParts.length > 0 ) {

      return map$( textParts, textPart => textPart.preText(_this).generateOutputFigure$() ) .then( r => {

      const figures = _toPrimitives(r);

      const ag = _this._anchor;
      const pos = ag._pos;

      let prePin = null;
      if( ag._absolute ) {
        prePin = pos.clone();
      }
      else {
        const vectorBounds = Bounds.union( invokeMap(figures,'vectorBounds') );
        prePin = vectorBounds.interpolate( pos );
      }

      return { figures: figures, anchor: prePin };
      });
    }
    else { // Simple text

      return _this.fontData$() .then( () => {

      const textLayout = TextLayout(_this);

      const prePin = textLayout.prePin();

      const textLines = textLayout._textLines;
      if( textLines.length === 0 || ( textLines.length === 1 && textLines[0]._chars.length === 0 ) ){
        return { figures: [], anchor: prePin };
      }

      // Keep a reference to the textLayout, we are going to use it in the tools
      _this._textLayout = textLayout;

      return { figures: [ textLayout ], anchor: prePin };
      });
    }
  },

  charAtPoint_( point ) {
    if( this.isEmpty ) {
      return null;
    }
    const charContainsPoint = function(char) {
      return char._frame.transformed( matrix ).contains(point);
    };
    const layout = this._textLayout;
    const textLines = layout.textLines();
    const matrix = this.matrix_();
    for( let k = 0, kEnd = textLines.length; k < kEnd; ++k ) {
      const line = textLines[k];
      const char = find( line._chars, charContainsPoint );
      if( char ) {
        return char;
      }
    }
    return null;
  },

// internal

  _clone() {
    const p = this.callParent();
    p.textParts = cloneDeep(this._textParts);
    return p;
  },

  clone( includingProcessor, partOfGroup, ungrouping ) {
    const t = this.callParent(includingProcessor,partOfGroup,ungrouping);

    const textLayout = this._textLayout;
    if( textLayout ) {
      t._textLayout = this._textLayout.clone();
      t._textLayout._text = t;
    }

    t._fontData      = this._fontData;
    t._fontDataScale = this._fontDataScale;
    return t;
  },

  outputsText() {
    return true;
  },

  init( p = {} ) {

    this.callParent(p);

    if( ! p.properties ) {
      this.properties = new TextProperties(p);
    }

    // Used while generating the outputFigure, kept for internal tools that
    // modifies the text layout without a full reprocessing (for example
    // CharsTransform)
    this._textLayout = null;
    this._fontData = null;
    this._fontDataScale = null;
    this._fontDataLines = null;

    // Change default, [TODO] idiom for this?
    if( this._textParts.length === 0 && p.brush === undefined ) {
      this.brush = new Brush({ color: Color.Black() });
    }

    if( ! p.anchor && ! p._anchor ) {
      this.anchor = new AnchorGenerator({pos:new Point(0.5,0),absolute:false});
    }
  },

  otherColors( colors) {
    const textParts = this._textParts;
    //_.forEach(textParts,function(tp) {
    for(let k=0,kEnd=textParts.length;k<kEnd;++k) {
      textParts[k].getColors(colors);
    }//);
    return colors;
  },
  otherPens( pens) {
    const textParts = this._textParts;
    //_.forEach(textParts,function(tp) {
    for(let k=0,kEnd=textParts.length;k<kEnd;++k) {
      textParts[k].getPens(pens);
    }//);
    return pens;
  },
  otherBrushes( brushes) {
    const textParts = this._textParts;
    //_.forEach(textParts,function(tp) {
    for(let k=0,kEnd=textParts.length;k<kEnd;++k) {
      textParts[k].getBrushes(brushes);
    }//);
    return brushes;
  },

  needsCacheToRender() {
    if(   Config.alwaysRenderLocally       ) { return false ; }
    if( ! Config.renderLocallyWhenPossible ) { return true ; }
    return ! this.powerClipsLocalRendering();
  },

  hitTest( point, options = {} ) {
    options.regionFrames = Config.useCharactersBoundsBasedSelection;
    if( this.canBeSelected() && this.globalFrame_().contains(point) ) { // [TODO] 'selectable'
      if( options.frameBased || ! Config.useCurvesBasedSelection ) {
        return true;
      }
      return this._processor.fineHitTest(point,this.globalMatrix_(),options) ;
    }
    return false;
  },

  correctZeroAdvance() { return true ; },
  
  lineDirectionVector() {
    return Matrix.rotate(this.lineDirection).apply_(1,0);
  },
  charDirectionVector() {
    return Matrix.rotate(this.charDirection).apply_(1,0);
  },

  isVertical() {
    const ld = this.lineDirectionVector();
    return Math.abs(ld.y) > Math.abs(ld.x);
  },

  doMultilineVerticalText() {
    return this.isVertical() && this.lines.length === 1 && this.verticalStyle === 'MultiLine';
  },

  effectiveLineDirection() {
    return this.doMultilineVerticalText() ? new Point(1,0) : this.lineDirectionVector();
  },

  effectiveLinesPerColumn( charsCount) {
    const linesPerColumn = this.linesPerColumn, columnCount = this.columnCount;
    return linesPerColumn > 0 ? linesPerColumn :
           columnCount    > 0 ? Math.ceil( charsCount / columnCount )
                              : null;
  },

  effectiveExtraLineSpacing_() {
    const fontHeight = this._fontData.height(this._fontDataScale);
    return Magnitude.absoluteMMValueFor(this.extraLineSpacing, fontHeight);
  },

  // [TODO] Review
  spaceWidth_() {
    return this._fontData.spaceWidth(this._fontDataScale);
  },
  ascent_() {
    return this._fontData.ascent(this._fontDataScale);
  },
  descent_() {
    return this._fontData.descent(this._fontDataScale);
  },
  sizeOfReferenceChar_() {
    return this._fontData.sizeOfReferenceChar(this._fontDataScale);
  },

  // True is chars are placed widthwise; false if they are placed heightwise.\n
  // When chars are placed widthwise, each next char is positioned based on their widths,
  // otherwise is position based on their heights.\n
  // Chars are placed widthwise whenever the 'CharDirection' is at least at 45ڠfrom
  // the 'LineDirection' (that is, when they are perpendicular to the flow of text).
  isWidthwise() {
    const c45Degrees = 0.707108679; // Sqrt( 2 ) / 2 ;
    return Math.abs( Point.dotProduct( this.charDirectionVector(), this.effectiveLineDirection() ) ) <= c45Degrees;
  },

  isLeftToRight() {
    const lineAngle = this.effectiveLineDirection().angle();
    return ( ( lineAngle <= Math.PI / 2.0 ) || ( lineAngle >= (3 / 2.0) * Math.PI ) );
  }
}));

Text.parseXml = function(node,bcFixes) {

  const text = new Text();

  Figure._baseParseXml.call(text, node, Text, bcFixes );

  // Each line is represented in CDL as a <Line>...</Line> entry
  text._text = joinLines( map( Xml.children(node,'Line'), 'textContent' ) );

  text.properties = TextProperties.parseXml(node);

  text.textParts = TextPart.arrayFromXml(node);

  return text;
};



/** WEBPACK FOOTER **
 ** ./src/cdl/figure/text/text.js
 **/